import { useTheme } from "@emotion/react";
import {
  AccountTreeOutlined,
  ExpandMore,
  TuneOutlined,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useRef, useState } from "react";
import api from "../../../../../../services/api";
import ErpChip from "../../../../../../components/erp/ErpChip";
import ErpFilter from "../../../../../../components/erp/ErpFilter";
import useErpSelecionado from "../../../../../../components/erp/useErpSelecionado";
import useStyles from "./styles";

// De onde vem o fluxo que o sistema está usando agora.
const ORIGENS = {
  CONFIGURACAO: { label: "Configurado", color: "success" },
  VARIAVEL_AMBIENTE: {
    label: "Variável de ambiente",
    color: "warning",
  },
  PADRAO: { label: "Padrão do sistema", color: "default" },
};

const labelFluxo = (fluxo) =>
  fluxo ? `${fluxo.id} - ${fluxo.descricao || ""}` : "";

// O campo mostra só o que está salvo nesta configuração (vazio = não
// configurado); o fluxo efetivo aparece em "Em uso".
const fluxoConfigurado = (dados) => {
  const id = dados?.idConfigurado;
  if (!id) return null;
  return (
    dados?.opcoes?.find((f) => f.id === id) || {
      id,
      descricao: "(não encontrado entre os fluxos ativos do ERP)",
    }
  );
};

export default function ConfigFluxoAtendimento() {
  const theme = useTheme();
  const styles = useStyles(theme);
  // Configuração de cada ERP (HUBSOFT A e B têm a sua): a tela mostra e salva
  // a do ERP escolhido.
  const { erps, erpId, setErpId, carregado, variosErps } = useErpSelecionado([
    "HUBSOFT",
  ]);
  const erpAtualRef = useRef(erpId);
  erpAtualRef.current = erpId;
  // Busca só depois da primeira abertura do accordion (e de novo ao trocar de ERP).
  const [jaAberto, setJaAberto] = useState(false);

  const [snackbar, setSnackbar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [config, setConfig] = useState(null);
  const [selecionado, setSelecionado] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  const aplicar = (dados) => {
    setConfig(dados);
    setSelecionado(fluxoConfigurado(dados));
  };

  const handleEdit = () => setIsEdit(true);

  const handleCancel = () => {
    setIsEdit(false);
    setSelecionado(fluxoConfigurado(config));
  };

  const getConfig = async () => {
    const erpPedido = erpId;
    setLoading(true);
    try {
      const response = await api.post("/interno/find/configFluxoAtendimento", {
        erpId: erpPedido,
      });
      if (erpPedido !== erpAtualRef.current) return; // trocou de ERP no meio
      aplicar(response?.data);
    } catch (error) {
      console.log(error);
      setSnackbar({
        children:
          error?.response?.data?.error ||
          "Erro ao buscar os fluxos de atendimento do ERP!",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!carregado || !jaAberto) return;
    setIsEdit(false);
    getConfig();
  }, [carregado, erpId, jaAberto]);

  const salvar = async () => {
    setSalvando(true);
    try {
      const response = await api.post(
        "/interno/update/configFluxoAtendimento",
        {
          erpId: config?.erp?.id || erpId,
          idFluxoAtendimento: selecionado?.id ?? null,
        },
      );
      aplicar(response?.data);
      setIsEdit(false);
      setSnackbar({
        children: selecionado
          ? "Fluxo de atendimento atualizado com sucesso!"
          : "",
        severity: "success",
      });
    } catch (error) {
      console.log(error);
      setSnackbar({
        children:
          error?.response?.data?.error ||
          "Erro ao salvar o fluxo de atendimento!",
        severity: "error",
      });
    } finally {
      setSalvando(false);
    }
  };

  const fluxoAtual = config?.opcoes?.find(
    (f) => f.id === config?.idFluxoAtendimento,
  );
  const origem = ORIGENS[config?.origem];
  const alterado =
    (selecionado?.id ?? null) !== (config?.idConfigurado ?? null);

  return (
    <>
      <Accordion
        data-secao="fluxo"
        onChange={(_, aberto) => aberto && setJaAberto(true)}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Box sx={styles.containerHeader}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TuneOutlined color="primary" />
              <Typography variant="h5" sx={{ ...styles.title }}>
                Configurações do fluxo de atendimento
              </Typography>
              {/* Para qual ERP é esta configuração. */}
              <ErpChip
                erp={
                  config?.erp ||
                  erps?.find((item) => item.id === erpId) || { tipo: "HUBSOFT" }
                }
              />
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {variosErps && (
            <ErpFilter
              erps={erps}
              value={erpId}
              onChange={(id) => id && setErpId(id)}
              limpavel={false}
              label="HubSoft"
              id="filtro-erp-fluxo-atendimento"
              sx={{ minWidth: 260, maxWidth: 360, mb: 1.5 }}
            />
          )}
          <Paper variant="outlined" sx={styles.paperBody}>
            {loading ? (
              <Box sx={styles.loading}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box>
                  <Box sx={styles.headerBox}>
                    <AccountTreeOutlined color="primary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Fluxo de atendimento da HubSoft
                    </Typography>
                  </Box>
                  <Typography sx={styles.subtitle}>
                    Fluxo usado pelo sistema para buscar, exibir e movimentar as
                    etapas dos atendimentos.
                    {config?.erp?.nome ? ` ${config.erp.nome}` : ""}.
                  </Typography>
                </Box>

                <Box sx={styles.atualBox}>
                  <Typography sx={styles.atualLabel}>Em uso:</Typography>
                  <Typography sx={styles.atualValor}>
                    {fluxoAtual
                      ? labelFluxo(fluxoAtual)
                      : config?.idFluxoAtendimento
                        ? `${config.idFluxoAtendimento} - (não encontrado entre os fluxos ativos do ERP)`
                        : "—"}
                  </Typography>
                  {origem && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={origem.color}
                      label={origem.label}
                    />
                  )}
                </Box>

                {!config?.erp && (
                  <Alert severity="warning" sx={styles.alert}>
                    Para salvar, cadastre o ERP HubSoft em Administração › Geral
                    › Configuração interna.
                  </Alert>
                )}

                <Autocomplete
                  size="small"
                  options={config?.opcoes || []}
                  value={selecionado}
                  disabled={!isEdit || salvando || !config?.erp}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  getOptionLabel={labelFluxo}
                  onChange={(event, value) => setSelecionado(value)}
                  noOptionsText="Nenhum fluxo ativo encontrado no ERP"
                  sx={styles.autocomplete}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      variant="filled"
                      label="Selecione o fluxo de atendimento"
                      data-busca="fluxo-select"
                      placeholder="Não configurado"
                      InputLabelProps={{ shrink: true }}
                      helperText=""
                    />
                  )}
                />
              </>
            )}
          </Paper>

          {!loading && (
            <Box sx={styles.boxActions}>
              {!isEdit ? (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleEdit}
                  disabled={!config?.erp}
                >
                  Editar
                </Button>
              ) : (
                <Box sx={styles.boxEditActions}>
                  <Button
                    onClick={handleCancel}
                    size="small"
                    variant="outlined"
                    disabled={salvando}
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={salvar}
                    disabled={!alterado || salvando}
                    startIcon={
                      salvando && <CircularProgress size={14} color="inherit" />
                    }
                  >
                    Salvar
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={4000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </>
  );
}
